/* eslint-disable @next/next/no-img-element */
"use client"

import type React from "react"
import { useState, startTransition } from "react"
import { useRouter } from "next/navigation"
import { useDispatch } from "react-redux"
import Cookies from 'js-cookie';

import CustomButton from "@/components/inputElements/ui/customButton"
import NProgress from "nprogress"
import { setAccountType } from "@/redux/slices/accountTypeSlice"
import { setReferralData } from "@/redux/slices/marketingReferralDataSlice"

import { clearUser } from "@/redux/slices/userSlice";
import { clearBasicInfo } from "@/redux/slices/basicInfoSlice";
import { clearAccountType } from "@/redux/slices/accountTypeSlice";
import { clearContactInfo } from "@/redux/slices/contactInfoSlice";
import { clearPersonalInfo } from "@/redux/slices/personalInfoSlice";
import { clearAllChecks } from "@/redux/slices/checkerSlice";
import { resetBusinessType } from "@/redux/slices/businessTypeSlice";
import { resetCACVerification } from "@/redux/slices/cacVerificationSlice";
import { resetCompanyDetails } from "@/redux/slices/companyDetailsSlice";
import { resetRepresentativeDetails } from "@/redux/slices/representativeDetailsSlice";


interface AccountTypeSelectionProps {
  onBack?: () => void
  onAccountTypeSelect?: (accountType: "personal" | "business") => void
  referral_code?: string | null
}

const AccountTypeSelection: React.FC<AccountTypeSelectionProps> = ({
  onBack,
  onAccountTypeSelect,
  referral_code,
}) => {
  const [selectedAccountType, setSelectedAccountType] = useState<"personal" | "business" | null>(null)
  const router = useRouter()

  const dispatch = useDispatch()
  const [isNavigating, setIsNavigating] = useState(false)
  const handleAccountTypeClick = (type: "personal" | "business") => {
    setSelectedAccountType(type)
    Cookies.remove("qwid-token");
    Cookies.remove("deviceId");
    Cookies.remove("persist:root");

    // Clear Redux slices
    dispatch(clearBasicInfo());
    dispatch(clearUser());
    dispatch(clearAllChecks());
    dispatch(clearContactInfo());
    dispatch(clearPersonalInfo());
    dispatch(resetBusinessType());
    dispatch(resetCACVerification());
    dispatch(resetCompanyDetails());
    dispatch(resetRepresentativeDetails());
  }

  const handleAccountTypeSelect = async (type: "personal" | "business") => {
    dispatch(setAccountType(type))

    if (type === "personal") {
      setIsNavigating(true)
      dispatch(setReferralData(referral_code ?? ""))
      NProgress.start()
      startTransition(() => {
        router.push("/onboarding/personal")
      })


    }
    if (type === "business") {
      setIsNavigating(true)
      dispatch(setReferralData(referral_code ?? ""))
      NProgress.start()
      startTransition(() => {
        router.push("/onboarding/business")
      })
    }
  }

  const handleGetStarted = () => {
    if (selectedAccountType) {
      handleAccountTypeSelect(selectedAccountType)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto text-center">
      <div className="mb-8">
        <h1 className="text-2xl font-normal text-gray-900 mb-4 leading-tight">
          What kind of account would you like to open?
        </h1>

        <p className="text-gray-500 text-[0.9rem] leading-relaxed hidden">
          What kind of account would you like to open today?
        </p>
      </div>

      <div className="space-y-4 mb-8">
        {/* Personal Account Option */}
        <div
          onClick={() => handleAccountTypeClick("personal")}
          className={`w-full p-6 rounded-xl border-[1px] transition-all duration-200 cursor-pointer ${selectedAccountType === "personal" ? "border-blue-500 " : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center`}
              >
                <img src="/assets/auth/personal.png" />
                {/* <User size={16} /> */}
              </div>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-[0.9rem] font-normal text-gray-900 mb-2">Personal account</h3>
              <p className="text-gray-500 text-[0.8rem]">
                Send, spend, and receive money from around the world.
              </p>
            </div>
          </div>
        </div>

        {/* Business Account Option */}
        <div
          onClick={() => handleAccountTypeClick("business")}
          className={`w-full p-6 rounded-xl border-[1px] transition-all duration-200 cursor-pointer ${selectedAccountType === "business" ? "border-blue-500" : "border-gray-200 hover:border-gray-300"
            }`}
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center `}
              >
                <img src="/assets/auth/bussiness.png" />
                {/* <Building size={16} /> */}
              </div>
            </div>
            <div className="flex-1 text-left">
              <h3 className="text-[0.9rem] font-normal text-gray-900 mb-2">Business account</h3>
              <p className="text-gray-500 text-sm">Do business or work as a freelancer internationally.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Get Started Button */}
      <div className="mb-8">
        <CustomButton
          type="button"
          variant={selectedAccountType && !isNavigating ? "primary" : "disabled"}
          disabled={!selectedAccountType || isNavigating}
          onClick={handleGetStarted}
        >
          Get Started
        </CustomButton>
      </div>

      {/* Back Button */}
      {onBack && (
        <div className="text-center">
          <CustomButton variant="lightback" onClick={onBack} className="mt-2 mb-7">
            Back
          </CustomButton>
        </div>
      )}
    </div>
  )
}

export default AccountTypeSelection
